import Utils from '@/common/utils/utils';

/**
 * Latest mainLanguage / mainLanguageSetBy of the logged-in user as last seen from the server
 * (CR-2026-005).
 *
 * The userDetail atom is not refreshed when someone else (an admin) changes this user, so the
 * screens that save the whole userSetting from the atom (admin mode switch, panel size) would
 * write the old main language back. They overlay this cache before saving instead. A module
 * variable, not state: updating it must not re-render anything.
 *
 * The cache belongs to one user record: it is only applied to the same userId, so it can never
 * leak to another user after a sign-out without page reload, nor to the record of another org
 * (one email has one record per org, each with its own main language).
 */
let latestMainLanguageSetting = null;

const MAIN_LANGUAGE_KEYS = ['mainLanguage', 'mainLanguageSetBy'];

/** Remember the main language keys of a userSetting received from the server for userId. */
export const rememberLatestMainLanguageSetting = (userId, userSetting) => {
    const setting = Utils.convertJSONDataToObject(userSetting);
    if (!userId || !setting?.mainLanguage) return;
    const values = {};
    MAIN_LANGUAGE_KEYS.forEach(key => {
        if (setting[key] !== undefined) values[key] = setting[key];
    });
    latestMainLanguageSetting = { userId, values };
};

/**
 * Copy of userSetting (object) of userId with the main language keys replaced by the latest
 * server values. Nothing remembered for this userId -> userSetting is returned unchanged
 * (behaviour before CR-2026-005).
 */
export const overlayLatestMainLanguageSetting = (userSetting, userId) => {
    if (!userId || latestMainLanguageSetting?.userId !== userId) return userSetting;
    const result = { ...(userSetting || {}) };
    MAIN_LANGUAGE_KEYS.forEach(key => delete result[key]);
    return { ...result, ...latestMainLanguageSetting.values };
};

/** Sign-out / tests: forget the remembered values. */
export const resetLatestMainLanguageSetting = () => {
    latestMainLanguageSetting = null;
};
